import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  School,
  Trophy,
  Users,
  Globe2,
  Building2,
  Award,
  BadgeCheck,
  Sparkles,
  BookOpen,
  Calculator,
  Atom,
  Monitor,
  Globe,
  Sigma,
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  PhoneCall,
  ClipboardList,
  Handshake,
  CalendarCheck,
  GraduationCap,
  Star,
  ShieldCheck,
  Quote,
  Newspaper,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/SchoolsHeader";
import Footer from "../components/SchoolsFooter";
import StudentVideosSection from "../components/StudentVideosSection";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

// ---- Data (all static) ----
const STATS = [
  { value: "25+", label: "Years of Excellence", icon: Award, color: "text-amber-400" },
  { value: "40M+", label: "Students Reached", icon: Users, color: "text-sky-400" },
  { value: "14M+", label: "Exams Conducted", icon: Trophy, color: "text-pink-400" },
  { value: "300+", label: "Partner Schools", icon: Building2, color: "text-emerald-400" },
  { value: "15+", label: "Countries", icon: Globe2, color: "text-purple-400" },
];

const OLYMPIADS = [
  { name: "NSTSE", full: "Science Talent Search", icon: Atom, color: "from-purple-500 to-fuchsia-500", link: "/exam/nstse/offline" },
  { name: "UIMO", full: "International Maths Olympiad", icon: Calculator, color: "from-emerald-500 to-green-500", link: "/exam/uimo/offline" },
  { name: "UCO", full: "Unified Cyber Olympiad", icon: Monitor, color: "from-sky-500 to-blue-500", link: "/exam/uco/offline" },
  { name: "UIEO", full: "International English Olympiad", icon: BookOpen, color: "from-pink-500 to-rose-500", link: "/exam/uieo" },
  { name: "UIGKO", full: "General Knowledge Olympiad", icon: Globe, color: "from-amber-500 to-orange-500", link: "/exam/uigko" },
  { name: "UMO", full: "Unified Maths Olympiad", icon: Sigma, color: "from-orange-500 to-red-500", link: "/exam/umo" },
];

const ADVANTAGES = [
  { feature: "Skill-based Testing", detail: "Measures how well concepts are learnt — not just what's memorised." },
  { feature: "Yardstick for Progress", detail: "Lets each student and teacher know where the student really stands." },
  { feature: "Detailed Diagnostic Report", detail: "Skill-wise graph, class-average and national ranking — free of cost." },
  { feature: "Affordable Fee", detail: "Nominal ₹150 per student — great value for a nation-wide Olympiad." },
  { feature: "Recognition for Schools", detail: "Zone-level & state-level awards for your Principal and Teachers." },
  { feature: "Holistic Development", detail: "Reasoning, critical thinking and language — beyond the syllabus." },
];

const SCHOOL_AWARDS = [
  {
    title: "Best Performance School Award",
    color: "from-amber-500 to-yellow-500",
    icon: Trophy,
    perks: [
      "One school per zone (17 zones)",
      "₹3,000 cash prize to Principal + Teacher-in-charge",
      "Best Contribution in Education certificate",
    ],
  },
  {
    title: "Top Mentor Award (State)",
    color: "from-sky-500 to-indigo-500",
    icon: BadgeCheck,
    perks: [
      "Top 5 schools per state (min. 300 schools participation)",
      "₹1,500 cash prize + Certificate",
      "For Principal and Teacher-in-charge",
    ],
  },
  {
    title: "Best Participation School",
    color: "from-emerald-500 to-teal-500",
    icon: Star,
    perks: [
      "Any school forwarding 100+ applications",
      "Certificate of Appreciation",
      "Every co-ordinator gets an appreciation certificate",
    ],
  },
];

const PROCESS = [
  { icon: ClipboardList, title: "Enquire", detail: "Fill the enquiry form or call us — we reply within 24 hours." },
  { icon: Handshake, title: "Onboard", detail: "We share brochures, syllabus and the school registration kit." },
  { icon: CalendarCheck, title: "Schedule", detail: "Pick the offline exam date that suits your academic calendar." },
  { icon: GraduationCap, title: "Conduct", detail: "We conduct at your school premises — you distribute the awards." },
];

const TESTIMONIALS = [
  {
    quote:
      "Unified Council deserves the number 1 ranking in the conduction of national level exams. The entire process is smooth and hassle-free.",
    name: "Mr. Ganesh Tiwari",
    school: "Priceless Pearl Scholars Academy, Nasik",
  },
  {
    quote:
      "The examinations undoubtedly inculcate the fundamentals of education and bring out an effective change in the talent pool of the students.",
    name: "Ms. Namrata Gupta",
    school: "Bishop Conrod Sr. Sec. School, Dohna",
  },
  {
    quote:
      "The standard of the question papers are exceptional. Unified Council is doing a tremendous job in moulding young talent.",
    name: "Mrs. Seema Sharma",
    school: "Nehru International Public School, Noida",
  },
];

const TRUST_BADGES = [
  { label: "ISO 9001 Certified" },
  { label: "Techfest IIT Bombay Partner" },
];

const MEDIA_LOGOS = ["Times of India", "Ind Today", "Social News", "Pinterest News", "DD Telangana", "TV5", "V6", "ETV", "Zee News", "99 TV"];

const SchoolsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = "For Schools — Bring Unified Council Olympiads to Your Campus";
  }, []);

  const [form, setForm] = useState({
    school: "",
    principal: "",
    phone: "",
    email: "",
    city: "",
    students: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.school.trim() || !form.principal.trim()) return "School name and Principal name are required.";
    if (!/^\d{10}$/.test(form.phone.trim())) return "Please enter a valid 10-digit phone number.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address.";
    if (!form.city.trim()) return "City is required.";
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Enquiry received! Our team will reach out within 24 hours.");
      setForm({ school: "", principal: "", phone: "", email: "", city: "", students: "", message: "" });
      setSubmitting(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="schools-page">
      <Header />

      {/* HERO */}
      <section className="relative min-h-[760px] overflow-hidden bg-[#05091b] text-white">
        {/* Ambient glow */}
        <div className="absolute -top-24 left-1/4 w-[500px] h-[500px] bg-sky-500/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 w-[500px] h-[500px] bg-fuchsia-500/20 rounded-full blur-3xl" />
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-10 md:px-8 md:py-16 lg:grid-cols-[1.15fr_1fr] lg:gap-16 lg:py-20">
          <div>
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur">
              <School className="w-4 h-4 text-sky-400" />
              <span className="text-xs font-black uppercase tracking-wider">
                For Schools & Educators
              </span>
            </div>

            <h1 className="mb-7 text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-[4.45rem]">
              Bring{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-pink-400 to-sky-400">
                India&apos;s most<br className="hidden lg:block" /> trusted Olympiads
              </span>{" "}
              <br className="hidden lg:block" />to your school.
            </h1>
            <p className="text-lg md:text-xl text-slate-300 font-medium max-w-2xl mb-8 leading-relaxed">
              For over <span className="text-white font-black">25 years</span>, Unified Council has partnered with{" "}
              <span className="text-white font-black">300+ schools</span> across{" "}
              <span className="text-white font-black">15+ countries</span> — conducting{" "}
              <span className="text-white font-black">14 million+</span> Olympiad exams. Give your students the same
              stage.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  const el = document.getElementById("enquire");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-full bg-white px-7 py-6 font-black text-slate-900 hover:bg-slate-100"
                data-testid="schools-hero-enquire"
              >
                <Send className="w-4 h-4 mr-2" /> School Enquiry
              </Button>
              <a
                href="tel:+919030007881"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-7 py-3 text-sm font-black transition-colors hover:bg-white/10"
                data-testid="schools-hero-call"
              >
                <PhoneCall className="w-4 h-4" /> Call 90300 07881
              </a>
              <a
                href="https://wa.me/919030007881?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%20Unified%20Council%20Olympiads%20for%20my%20school."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3 text-sm font-black transition-colors hover:bg-emerald-600"
                data-testid="schools-hero-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>

            {/* Mini stats row */}
            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-5">
              {STATS.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                    <Icon className={`w-4 h-4 mb-1.5 ${s.color}`} />
                    <p className="text-xl font-black">{s.value}</p>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-tight">
                      {s.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual */}
          <div className="relative hidden lg:block">
            <div className="absolute -inset-4 bg-gradient-to-br from-sky-500/30 to-fuchsia-500/30 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-slate-800 to-slate-900 p-7 shadow-2xl">
              <img
                src="https://images.pexels.com/photos/5212700/pexels-photo-5212700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Happy students and teacher"
                className="h-[360px] w-full rounded-2xl object-cover"
              />
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-emerald-500/15 border border-emerald-500/25 p-3">
                  <p className="text-2xl font-black text-emerald-400">98%</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Retention</p>
                </div>
                <div className="rounded-xl bg-sky-500/15 border border-sky-500/25 p-3">
                  <p className="text-2xl font-black text-sky-400">17</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Zones</p>
                </div>
                <div className="rounded-xl bg-amber-500/15 border border-amber-500/25 p-3">
                  <p className="text-2xl font-black text-amber-400">₹5 Cr+</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Prizes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="border-y border-slate-100 bg-slate-50/60 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center gap-6 md:gap-10 justify-between">
          <p className="text-sm font-black uppercase tracking-wider text-slate-500">
            Trusted by educators & recognised nationally
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {TRUST_BADGES.map((b) => (
              <div
                key={b.label}
                className="inline-flex items-center rounded-full bg-white border-2 border-slate-100 px-4 py-2 text-sm font-black text-slate-700 shadow-sm"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-500 mr-2" />
                {b.label}
              </div>
            ))}
            <div className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-slate-100 px-4 py-2 shadow-sm">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-black text-slate-700">Times of India recognised</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 bg-sky-50 border-2 border-sky-100 rounded-full px-3 py-1 mb-3">
              <Sparkles className="w-4 h-4 text-sky-600" />
              <span className="text-xs font-black uppercase tracking-wider text-sky-600">Why Unified Council</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
              Scientifically designed. <span className="text-gradient">School-friendly.</span>
            </h2>
            <p className="mt-3 text-slate-600 font-medium md:text-lg">
              Our Olympiads are built on a deep study of CBSE, ICSE, ISC and State Board syllabi — testing skills that
              really matter, not rote memory.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ADVANTAGES.map((a, i) => (
              <div
                key={a.feature}
                className="relative bg-white rounded-2xl border-2 border-slate-100 p-6 hover:-translate-y-1 hover:shadow-xl hover:border-slate-200 transition-all group"
              >
                <div
                  className={`w-11 h-11 rounded-xl bg-gradient-to-br mb-4 flex items-center justify-center text-white ${
                    ["from-sky-500 to-indigo-500", "from-pink-500 to-rose-500", "from-emerald-500 to-teal-500", "from-amber-500 to-orange-500", "from-purple-500 to-fuchsia-500", "from-cyan-500 to-blue-500"][i % 6]
                  } group-hover:scale-110 transition-transform`}
                >
                  <BadgeCheck className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">{a.feature}</h3>
                <p className="text-sm text-slate-600 font-medium leading-relaxed">{a.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OLYMPIADS OFFERED */}
      <section className="py-16 md:py-20 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border-2 border-amber-100 rounded-full px-3 py-1 mb-3">
                <Trophy className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-black uppercase tracking-wider text-amber-600">6 Olympiads</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                One partner. Every subject covered.
              </h2>
            </div>
            <p className="text-sm text-slate-600 font-medium max-w-md">
              Conduct any or all of our Olympiads at your school. One coordinator handles every logistic.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {OLYMPIADS.map((o) => {
              const Icon = o.icon;
              return (
                <button
                  key={o.name}
                  onClick={() => navigate(o.link)}
                  data-testid={`schools-olympiad-${o.name.toLowerCase()}`}
                  className="group bg-white rounded-2xl border-2 border-slate-100 p-4 text-left hover:-translate-y-1 hover:shadow-xl hover:border-slate-200 transition-all"
                >
                  <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${o.color} flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-black text-slate-900">{o.name}</p>
                  <p className="text-[11px] text-slate-500 font-semibold leading-tight mt-0.5">{o.full}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* AWARDS FOR SCHOOLS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 bg-amber-50 border-2 border-amber-100 rounded-full px-3 py-1 mb-3">
              <Award className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-black uppercase tracking-wider text-amber-600">Awards for Schools</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
              Recognise your Principal and Teachers on a{" "}
              <span className="text-gradient">national stage</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {SCHOOL_AWARDS.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  className="relative rounded-2xl border-2 border-slate-100 bg-white p-6 hover:shadow-2xl hover:-translate-y-1 transition-all overflow-hidden group"
                >
                  <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${a.color}`} />
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${a.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 mb-3">{a.title}</h3>
                  <ul className="space-y-2">
                    {a.perks.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700">
                        <BadgeCheck className="w-4 h-4 mt-0.5 flex-none text-emerald-500" />
                        <span className="font-medium">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-20 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-sky-500/15 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-3">
              <span className="text-xs font-black uppercase tracking-wider text-sky-400">How It Works</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight">
              From enquiry to exam day —{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-pink-400">
                zero stress
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition-colors"
                >
                  <span className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-fuchsia-500 flex items-center justify-center font-black text-sm shadow-lg">
                    {i + 1}
                  </span>
                  <Icon className="w-6 h-6 text-sky-300 mb-3" />
                  <h3 className="text-lg font-black">{p.title}</h3>
                  <p className="text-sm text-slate-300 font-medium mt-1 leading-relaxed">{p.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 bg-pink-50 border-2 border-pink-100 rounded-full px-3 py-1 mb-3">
              <Quote className="w-4 h-4 text-pink-600" />
              <span className="text-xs font-black uppercase tracking-wider text-pink-600">Principals speak</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
              What Principals say about us
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="relative rounded-2xl bg-gradient-to-br from-slate-50 to-white border-2 border-slate-100 p-6 hover:shadow-xl transition-shadow"
              >
                <Quote className="w-8 h-8 text-slate-200 mb-3" />
                <p className="text-slate-700 font-medium leading-relaxed text-[15px]">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <p className="text-sm font-black text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500 font-semibold">{t.school}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT VIDEO TESTIMONIES */}
      <StudentVideosSection />

      {/* MEDIA */}
      <section className="py-12 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Newspaper className="w-4 h-4 text-slate-500" />
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">As featured in</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {MEDIA_LOGOS.map((m) => (
              <span
                key={m}
                className="inline-flex items-center rounded-full bg-white border-2 border-slate-100 px-4 py-2 text-xs font-black text-slate-600 hover:border-slate-200 hover:text-slate-900 transition-colors"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquire" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-50 border-2 border-emerald-100 rounded-full px-3 py-1 mb-3">
              <Send className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-black uppercase tracking-wider text-emerald-600">School Enquiry</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4">
              Talk to us — we&apos;ll get your school onboarded in{" "}
              <span className="text-gradient">under a week</span>
            </h2>
            <p className="text-slate-600 font-medium mb-8 md:text-lg">
              Fill this quick form. Our team will call you within 24 hours with the brochure, dates and school-registration
              kit.
            </p>

            <div className="space-y-3">
              <a
                href="tel:+919030007881"
                className="flex items-center gap-3 rounded-2xl border-2 border-slate-100 bg-white p-4 hover:border-sky-200 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-black text-slate-500">Call us</p>
                  <p className="text-base font-black text-slate-900">90300 07881 · 040-4014 7035</p>
                </div>
              </a>
              <a
                href="mailto:support@unifiedcouncil.com"
                className="flex items-center gap-3 rounded-2xl border-2 border-slate-100 bg-white p-4 hover:border-pink-200 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-black text-slate-500">Email us</p>
                  <p className="text-base font-black text-slate-900">support@unifiedcouncil.com</p>
                </div>
              </a>
              <a
                href="https://wa.me/919030007881?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%20Unified%20Council%20Olympiads%20for%20my%20school."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl border-2 border-slate-100 bg-white p-4 hover:border-emerald-200 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-black text-slate-500">WhatsApp</p>
                  <p className="text-base font-black text-slate-900">Instant reply</p>
                </div>
              </a>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border-2 border-slate-100 bg-white p-6 md:p-8 shadow-sm"
            data-testid="schools-enquiry-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="School Name *" name="school" value={form.school} onChange={handleChange} placeholder="e.g. Kennedy High School" testid="input-school" />
              <FormField label="Principal Name *" name="principal" value={form.principal} onChange={handleChange} placeholder="Dr. / Mr. / Ms." testid="input-principal" />
              <FormField label="Phone (10 digits) *" name="phone" value={form.phone} onChange={handleChange} placeholder="9876543210" testid="input-phone" />
              <FormField label="Email" name="email" value={form.email} onChange={handleChange} placeholder="school@example.com" testid="input-email" type="email" />
              <FormField label="City *" name="city" value={form.city} onChange={handleChange} placeholder="Hyderabad" testid="input-city" />
              <FormField label="Approx. Students" name="students" value={form.students} onChange={handleChange} placeholder="e.g. 200" testid="input-students" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-black uppercase tracking-wider text-slate-600 mb-1.5 block">
                Anything else?
              </label>
              <Textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Which Olympiads are you interested in? Preferred exam window?"
                rows={3}
                className="rounded-xl border-2 border-slate-100 focus:border-sky-300"
                data-testid="input-message"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full rounded-full font-black py-6 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="submit-enquiry"
            >
              {submitting ? "Sending…" : (
                <>
                  Send Enquiry
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="mt-3 text-xs text-slate-500 font-semibold text-center">
              We respect your privacy. No spam — just Olympiad updates.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FormField = ({ label, name, value, onChange, placeholder, testid, type = "text" }) => (
  <div>
    <label className="text-xs font-black uppercase tracking-wider text-slate-600 mb-1.5 block">{label}</label>
    <Input
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      className="rounded-xl border-2 border-slate-100 focus:border-sky-300 h-11"
      data-testid={testid}
    />
  </div>
);

export default SchoolsPage;
